import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Inbox, Plus, Phone, Settings2, Trash2 } from "lucide-react";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { CustomerManageDialog } from "./CustomerManageDialog";
import { fmtDateOnly, fmtTime12 } from "./leadUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "bg-[#0a0a0a] border-zinc-800 text-white h-10 rounded-sm placeholder:text-zinc-600";
const selectCls = "h-10 bg-[#0a0a0a] border border-zinc-800 text-white rounded-sm px-3 text-sm";

const AddCustomerDialog = ({ open, onOpenChange, onCreated }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", referral_source: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.name || !form.phone) return toast.error("Name and phone are required.");
    setSaving(true);
    try {
      await axios.post(`${API}/admin/customers`, form);
      toast.success("Customer added");
      onOpenChange(false);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add customer");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Add Customer</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {[["name", "Name *"], ["phone", "Phone *"], ["email", "Email"], ["address", "Service Address"], ["referral_source", "How did they hear about us?"]].map(([k, label]) => (
            <Input key={k} data-testid={`add-customer-${k}`} className={inputCls} placeholder={label} value={form[k]} onChange={set(k)} />
          ))}
        </div>
        <DialogFooter>
          <button data-testid="add-customer-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-sm px-6 py-2.5 font-semibold">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save Customer
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const CustomersTab = ({ onAuthError }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [agreementFilter, setAgreementFilter] = useState("All");
  const [upcomingFilter, setUpcomingFilter] = useState("All");
  const [manageCustomer, setManageCustomer] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/customers`);
      setCustomers(res.data);
    } catch (err) {
      if (!onAuthError(err)) toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  }, [onAuthError]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const removeCustomer = async (c) => {
    if (!window.confirm(`Delete customer ${c.name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/customers/${c.customer_id}`);
      toast.success("Customer deleted");
      fetchCustomers();
    } catch (err) {
      if (!onAuthError(err)) toast.error("Delete failed");
    }
  };

  const filtered = customers.filter((c) => {
    const q = search.toLowerCase();
    if (q && !`${c.name} ${c.phone} ${c.email || ""} ${c.referral_source || ""}`.toLowerCase().includes(q)) return false;
    if (agreementFilter !== "All" && (c.agreement_status || "Pending") !== agreementFilter) return false;
    if (upcomingFilter === "Upcoming" && !c.upcoming_appointment) return false;
    if (upcomingFilter === "Recurring" && !c.has_recurring) return false;
    return true;
  });

  return (
    <div data-testid="customers-tab">
      <div className="flex flex-wrap gap-3 mb-6 items-center">
        <Input data-testid="customers-search" className={`${inputCls} max-w-xs`} placeholder="Search name, phone, email..." value={search} onChange={(e) => setSearch(e.target.value)} />
        <select data-testid="customers-filter-agreement" className={selectCls} value={agreementFilter} onChange={(e) => setAgreementFilter(e.target.value)}>
          <option value="All">Agreement: All</option><option>Accepted</option><option>Pending</option>
        </select>
        <select data-testid="customers-filter-type" className={selectCls} value={upcomingFilter} onChange={(e) => setUpcomingFilter(e.target.value)}>
          <option value="All">All Customers</option>
          <option value="Upcoming">Has Upcoming Appointment</option>
          <option value="Recurring">On Recurring Reminders</option>
        </select>
        <button data-testid="add-customer-button" onClick={() => setAddOpen(true)} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-4 h-10 text-sm font-semibold ml-auto">
          <Plus className="h-4 w-4" /> Add Customer
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>
      ) : filtered.length === 0 ? (
        <div data-testid="customers-empty-state" className="text-center py-24 text-zinc-500">
          <Inbox className="h-12 w-12 mx-auto mb-4 text-zinc-700" />
          No customers yet. Convert a lead or add one manually.
        </div>
      ) : (
        <div className="space-y-3" data-testid="customers-list">
          {filtered.map((c) => (
            <div key={c.customer_id} data-testid={`customer-row-${c.customer_id}`} className="bg-[#121212] border border-zinc-800 rounded-xl p-4 sm:p-5 grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
              <div className="lg:col-span-3">
                <div className="font-heading font-semibold text-white">{c.name}</div>
                <a href={`tel:${c.phone.replace(/[^+\d]/g, "")}`} className="inline-flex items-center gap-1.5 text-blue-400 hover:text-blue-300 text-sm"><Phone className="h-3.5 w-3.5" /> {c.phone}</a>
                {c.email && <div className="text-zinc-500 text-xs break-all">{c.email}</div>}
              </div>
              <div className="lg:col-span-3 text-xs text-zinc-400">
                {c.referral_source && <div>Source: {c.referral_source}</div>}
                <div>Requested: {(c.services_requested || []).slice(0, 2).join(", ") || "—"}</div>
                <div>Completed: {(c.services_completed || []).length} service{(c.services_completed || []).length === 1 ? "" : "s"}</div>
              </div>
              <div className="lg:col-span-3 text-xs">
                {c.upcoming_appointment ? (
                  <div className="text-emerald-400">Next: {fmtDateOnly(c.upcoming_appointment.date)} {fmtTime12(c.upcoming_appointment.start_time)} — {c.upcoming_appointment.service_type}</div>
                ) : (
                  <div className="text-zinc-600">No upcoming appointment</div>
                )}
                {c.has_recurring && <div className="text-cyan-400 mt-0.5">On recurring reminders</div>}
              </div>
              <div className="lg:col-span-3 flex items-center gap-2 flex-wrap lg:justify-end">
                <Badge variant="outline" className={`rounded-sm ${c.agreement_status === "Accepted" ? "bg-green-600/20 text-green-400 border-green-500/40" : "bg-amber-600/20 text-amber-400 border-amber-500/40"}`}>
                  Agreement {c.agreement_status || "Pending"}
                </Badge>
                <button data-testid={`customer-manage-${c.customer_id}`} onClick={() => setManageCustomer(c)} className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-3 h-9 text-xs font-semibold">
                  <Settings2 className="h-3.5 w-3.5" /> Manage
                </button>
                <button data-testid={`customer-delete-${c.customer_id}`} onClick={() => removeCustomer(c)} className="p-2 border border-zinc-800 rounded-sm text-zinc-500 hover:text-red-400" title="Delete customer">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {manageCustomer && (
        <CustomerManageDialog
          key={manageCustomer.customer_id}
          customer={manageCustomer}
          onClose={() => setManageCustomer(null)}
          onChanged={fetchCustomers}
        />
      )}
      {addOpen && <AddCustomerDialog open={addOpen} onOpenChange={setAddOpen} onCreated={fetchCustomers} />}
    </div>
  );
};
